/**
 * AuthOtpStep — the "enter the code we sent you" step of an auth flow.
 *
 * `OtpInput` already owns the behaviour (countdown, resend, shake-and-clear
 * on a wrong code). What the three OTP screens repeated was the placement:
 * the same entrance at STAGGER * 4, wrapping the same five props. This is
 * that placement, once.
 */
import type { ReactElement } from 'react'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { OtpInput } from '../ui/OtpInput'
import { DURATION, STAGGER } from '../../lib/theme/tokens'

interface AuthOtpStepProps {
  onComplete: (code: string) => Promise<void>
  onResend: () => Promise<void>
  /** True while a verify or resend request is in flight. */
  disabled?: boolean
  /** Wrong-code / expired-code message, rendered under the boxes. */
  error?: string
  /** Screen-scoped id, e.g. `verify-phone.otp-input`. Maestro selects on it. */
  testID: string
}

export function AuthOtpStep({
  onComplete,
  onResend,
  disabled,
  error,
  testID,
}: AuthOtpStepProps): ReactElement {
  const reducedMotion = useReducedMotion()

  return (
    <Animated.View
      collapsable={false}
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)}
    >
      <OtpInput
        onComplete={onComplete}
        onResend={onResend}
        disabled={disabled}
        error={error}
        testID={testID}
      />
    </Animated.View>
  )
}
